import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MapPinIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  PhoneIcon,
  HomeIcon,
  UserIcon,
  ArrowLeftIcon,
  Bars3Icon,
  XMarkIcon,
  PlusIcon,
  UserPlusIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  FlagIcon,
  NoSymbolIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import VoterCheckIn from '../components/VoterCheckIn';
import { getRoute, checkInVoter, getCustomFields, addVoter, getCanvassingConfig } from '../services/api';
import { StarIcon } from '@heroicons/react/24/outline';

// Icon mapping for dynamic status buttons
const ICON_MAP = {
  CheckCircleIcon: CheckCircleIcon,
  HomeIcon: HomeIcon,
  DocumentTextIcon: DocumentTextIcon,
  FlagIcon: FlagIcon,
  NoSymbolIcon: NoSymbolIcon,
  PhoneIcon: PhoneIcon,
  ArrowRightIcon: ChevronRightIcon,
  UserIcon: UserIcon,
  StarIcon: StarIcon,
  XCircleIcon: XCircleIcon,
  ClockIcon: ClockIcon,
};

// Get icon component by name
function getIconComponent(iconName) {
  return ICON_MAP[iconName] || CheckCircleIcon;
}

// Get status display info from config or fallback
function getStatusDisplay(status, canvassingConfig) {
  const statusOption = canvassingConfig?.statusOptions?.find(opt => opt.id === status);
  if (statusOption) {
    return {
      label: statusOption.label,
      color: statusOption.color,
      bgClass: `bg-opacity-20`,
    };
  }
  // Fallback for legacy statuses
  const fallbackLabels = {
    pending: 'Pending',
    contacted: 'Contacted',
    not_home: 'Not Home',
    refused: 'Refused',
    moved: 'Moved',
    gave_flyer: 'Gave Flyer',
    put_sign: 'Put Up Sign',
    callback: 'Callback'
  };
  const fallbackColors = {
    pending: '#6B7280',
    contacted: '#10B981',
    not_home: '#F59E0B',
    refused: '#EF4444',
    moved: '#8B5CF6',
    gave_flyer: '#3B82F6',
    put_sign: '#6366F1',
    callback: '#F97316'
  };
  return {
    label: fallbackLabels[status] || status,
    color: fallbackColors[status] || '#6B7280',
    bgClass: 'bg-opacity-20',
  };
}

function StopCard({ stop, index, isActive, isCurrent, onClick, canvassingConfig }) {
  const status = stop.check_in?.status || 'pending';
  const statusDisplay = getStatusDisplay(status, canvassingConfig);

  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-3 rounded-lg border transition-all ${
        isCurrent
          ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
          : isActive
          ? 'border-gray-300 bg-white'
          : 'border-gray-200 bg-gray-50'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Stop number */}
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0 ${
            isCurrent && status === 'pending'
              ? 'bg-blue-500 text-white'
              : status === 'pending'
              ? 'bg-gray-200 text-gray-600'
              : 'text-white'
          }`}
          style={status !== 'pending' ? { backgroundColor: statusDisplay.color } : undefined}
        >
          {status !== 'pending' ? (
            <CheckCircleSolidIcon className="h-5 w-5" />
          ) : (
            index + 1
          )}
        </div>

        {/* Stop info */}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-gray-900 truncate">
            {stop.name || `Voter ${index + 1}`}
          </div>
          <div className="text-sm text-gray-500 truncate">
            {stop.address}
          </div>
          {stop.check_in && status !== 'pending' && (
            <div
              className="inline-flex items-center gap-1 mt-1 text-xs px-2 py-0.5 rounded"
              style={{
                backgroundColor: `${statusDisplay.color}20`,
                color: statusDisplay.color
              }}
            >
              {statusDisplay.label}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

function StopListDrawer({ stops, currentIndex, onStopSelect, isOpen, onClose, canvassingConfig }) {
  const completedCount = stops.filter(s => s.check_in?.status && s.check_in.status !== 'pending').length;

  return (
    <div
      className={`fixed inset-0 z-50 transition-opacity ${
        isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[80vh] overflow-hidden transform transition-transform ${
          isOpen ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h3 className="font-semibold text-gray-900">Route Stops</h3>
            <p className="text-sm text-gray-500">
              {completedCount} of {stops.length} completed
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-gray-200">
          <div
            className="h-full bg-green-500 transition-all"
            style={{ width: `${(completedCount / stops.length) * 100}%` }}
          />
        </div>

        {/* Stop list */}
        <div className="p-4 space-y-2 overflow-y-auto max-h-[60vh]">
          {stops.map((stop, index) => (
            <StopCard
              key={stop.voter_id || index}
              stop={stop}
              index={index}
              isActive={true}
              isCurrent={index === currentIndex}
              onClick={() => {
                onStopSelect(index);
                onClose();
              }}
              canvassingConfig={canvassingConfig}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// Add Voter Modal Component
function AddVoterModal({ datasetId, onClose, onSuccess, isLoading }) {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    party: '',
    notes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.address.trim()) return;
    onSuccess(formData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white w-full sm:max-w-md sm:rounded-xl rounded-t-xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <UserPlusIcon className="h-5 w-5 text-blue-600" />
            Add New Voter
          </h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4 overflow-y-auto max-h-[70vh]">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Full name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
              placeholder="Full address"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              required
            />
            <p className="mt-1 text-xs text-gray-500">Address will be geocoded automatically</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="Phone number"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Party Affiliation
            </label>
            <select
              value={formData.party}
              onChange={(e) => setFormData(prev => ({ ...prev, party: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Unknown</option>
              <option value="D">Democrat</option>
              <option value="R">Republican</option>
              <option value="I">Independent</option>
              <option value="L">Libertarian</option>
              <option value="G">Green</option>
              <option value="O">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Any additional notes..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </form>

        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isLoading || !formData.name.trim() || !formData.address.trim()}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <PlusIcon className="h-4 w-4" />
                  Add Voter
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CanvasserView() {
  const { routeId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // UI State
  const [currentStopIndex, setCurrentStopIndex] = useState(0);
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [showStopList, setShowStopList] = useState(false);
  const [showAddVoter, setShowAddVoter] = useState(false);

  // Fetch route data with polling for real-time sync
  const { data: routeData, isLoading: loadingRoute, dataUpdatedAt } = useQuery({
    queryKey: ['route', routeId],
    queryFn: () => getRoute(routeId),
    enabled: !!routeId,
    refetchInterval: 5000, // Poll every 5 seconds for real-time sync
    refetchIntervalInBackground: true // Keep syncing even when tab is not focused
  });

  // Fetch custom fields
  const { data: customFieldsData } = useQuery({
    queryKey: ['customFields'],
    queryFn: getCustomFields
  });

  const route = routeData?.route;
  const stops = route?.stops || [];
  const customFields = customFieldsData?.fields || [];
  const currentStop = stops[currentStopIndex];

  // Fetch canvassing config for this dataset
  const { data: canvassingConfigData } = useQuery({
    queryKey: ['canvassingConfig', route?.dataset_id],
    queryFn: () => getCanvassingConfig(route?.dataset_id),
    enabled: !!route?.dataset_id
  });
  const canvassingConfig = canvassingConfigData?.config;

  // Calculate progress
  const completedCount = useMemo(() => {
    return stops.filter(s => s.check_in?.status && s.check_in.status !== 'pending').length;
  }, [stops]);

  // Check-in mutation
  const checkInMutation = useMutation({
    mutationFn: ({ voterId, checkInData }) => checkInVoter(voterId, checkInData),
    onSuccess: () => {
      queryClient.invalidateQueries(['route', routeId]);
      setShowCheckIn(false);

      // Auto-advance to next stop if enabled (default: true) and not at end
      const shouldAutoAdvance = canvassingConfig?.autoAdvance !== false;
      if (shouldAutoAdvance && currentStopIndex < stops.length - 1) {
        setCurrentStopIndex(currentStopIndex + 1);
      }
    }
  });

  // Add voter mutation
  const addVoterMutation = useMutation({
    mutationFn: (voterData) => addVoter(route?.dataset_id, voterData),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['route', routeId]);
      setShowAddVoter(false);
      // Show success message or navigate to new voter
      alert(`Added new voter: ${data.voter?.name}`);
    },
    onError: (error) => {
      alert(`Failed to add voter: ${error.message}`);
    }
  });

  // Handle add voter submission
  const handleAddVoter = (voterData) => {
    addVoterMutation.mutate(voterData);
  };

  // Handle navigation
  const goToPrevious = () => {
    if (currentStopIndex > 0) {
      setCurrentStopIndex(currentStopIndex - 1);
    }
  };

  const goToNext = () => {
    if (currentStopIndex < stops.length - 1) {
      setCurrentStopIndex(currentStopIndex + 1);
    }
  };

  // Open directions in maps app
  const openDirections = () => {
    if (!currentStop) return;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${currentStop.lat},${currentStop.lng}&travelmode=walking`;
    window.open(url, '_blank');
  };

  // Handle check-in submission
  const handleCheckIn = (checkInData) => {
    if (!currentStop) return;
    checkInMutation.mutate({
      voterId: currentStop.voter_id,
      checkInData: {
        ...checkInData,
        route_id: routeId,
        stop_index: currentStopIndex
      }
    });
  };

  if (loadingRoute) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto" />
          <p className="mt-2 text-gray-600">Loading route...</p>
        </div>
      </div>
    );
  }

  if (!route || stops.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="text-center">
          <MapPinIcon className="h-12 w-12 text-gray-400 mx-auto" />
          <h2 className="mt-2 text-lg font-semibold text-gray-900">No Route Found</h2>
          <p className="mt-1 text-gray-600">This route doesn't exist or has no stops.</p>
          <button
            onClick={() => navigate('/map')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg"
          >
            Back to Map
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => navigate('/map')}
            className="p-2 -ml-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>

          <div className="text-center">
            <h1 className="font-semibold text-gray-900">
              Stop {currentStopIndex + 1} of {stops.length}
            </h1>
            <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
              <span>{completedCount} completed</span>
              {/* Sync status indicator */}
              <span className="flex items-center gap-1 text-green-600">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                Synced
              </span>
            </div>
          </div>

          <button
            onClick={() => setShowStopList(true)}
            className="p-2 -mr-2 text-gray-600 hover:text-gray-900"
          >
            <Bars3Icon className="h-5 w-5" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-gray-200">
          <div
            className="h-full bg-green-500 transition-all"
            style={{ width: `${(completedCount / stops.length) * 100}%` }}
          />
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 p-4">
        {currentStop && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {/* Voter info */}
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <UserIcon className="h-6 w-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {currentStop.name || 'Unknown Voter'}
                  </h2>
                  <div className="flex items-center gap-1 text-gray-500 mt-1">
                    <HomeIcon className="h-4 w-4" />
                    <span className="text-sm">{currentStop.address}</span>
                  </div>
                  {currentStop.phone && (
                    <div className="flex items-center gap-1 text-gray-500 mt-1">
                      <PhoneIcon className="h-4 w-4" />
                      <a
                        href={`tel:${currentStop.phone}`}
                        className="text-sm text-blue-600 hover:underline"
                      >
                        {currentStop.phone}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Voter details */}
              {(currentStop.party || currentStop.age) && (
                <div className="flex gap-2 mt-3">
                  {currentStop.party && (
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      currentStop.party === 'D' ? 'bg-blue-100 text-blue-700' :
                      currentStop.party === 'R' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {currentStop.party === 'D' ? 'Democrat' :
                       currentStop.party === 'R' ? 'Republican' :
                       currentStop.party}
                    </span>
                  )}
                  {currentStop.age && (
                    <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700">
                      Age {currentStop.age}
                    </span>
                  )}
                  {currentStop.previous_votes !== undefined && (
                    <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700">
                      {currentStop.previous_votes} votes
                    </span>
                  )}
                </div>
              )}

              {/* Current status */}
              {currentStop.check_in && currentStop.check_in.status !== 'pending' && (() => {
                const statusDisplay = getStatusDisplay(currentStop.check_in.status, canvassingConfig);
                return (
                  <div
                    className="mt-3 p-2 rounded-lg"
                    style={{
                      backgroundColor: `${statusDisplay.color}20`,
                      color: statusDisplay.color
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <CheckCircleIcon className="h-4 w-4" />
                      <span className="text-sm font-medium">
                        {statusDisplay.label}
                      </span>
                      {currentStop.check_in.timestamp && (
                        <span className="text-xs opacity-75">
                          at {new Date(currentStop.check_in.timestamp).toLocaleTimeString()}
                        </span>
                      )}
                    </div>
                    {currentStop.check_in.notes && (
                      <p className="mt-1 text-xs">{currentStop.check_in.notes}</p>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Actions - Mobile-first with large touch targets */}
            <div className="p-4 space-y-3">
              {/* Quick status buttons - dynamic grid from config */}
              <div className="grid grid-cols-3 gap-2">
                {(canvassingConfig?.statusOptions || [
                  { id: 'contacted', label: 'Contacted', color: '#10B981', icon: 'CheckCircleIcon' },
                  { id: 'not_home', label: 'Not Home', color: '#F59E0B', icon: 'XCircleIcon' },
                  { id: 'gave_flyer', label: 'Gave Flyer', color: '#3B82F6', icon: 'DocumentTextIcon' },
                  { id: 'put_sign', label: 'Put Sign', color: '#6366F1', icon: 'FlagIcon' },
                  { id: 'refused', label: 'Refused', color: '#EF4444', icon: 'NoSymbolIcon' },
                  { id: 'callback', label: 'Callback', color: '#F97316', icon: 'ClockIcon' }
                ]).map((statusOption) => {
                  const IconComponent = getIconComponent(statusOption.icon);
                  return (
                    <button
                      key={statusOption.id}
                      onClick={() => handleCheckIn({ status: statusOption.id })}
                      disabled={checkInMutation.isPending}
                      className="flex flex-col items-center justify-center gap-1 p-3 min-h-[72px] text-white rounded-xl font-medium active:scale-95 disabled:opacity-50 transition-transform"
                      style={{
                        backgroundColor: statusOption.color,
                        '--hover-bg': `${statusOption.color}dd`
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${statusOption.color}dd`}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = statusOption.color}
                    >
                      <IconComponent className="h-6 w-6" />
                      <span className="text-xs text-center leading-tight">{statusOption.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* More options & Directions */}
              <div className="flex gap-2">
                <button
                  onClick={() => setShowCheckIn(true)}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 active:scale-95 transition-transform"
                >
                  Add Notes
                </button>
                <button
                  onClick={openDirections}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 active:scale-95 transition-transform"
                >
                  <MapPinIcon className="h-5 w-5" />
                  Directions
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Navigation footer */}
      <footer className="bg-white border-t border-gray-200 p-4 safe-area-inset-bottom">
        <div className="flex items-center justify-between gap-4">
          <button
            onClick={goToPrevious}
            disabled={currentStopIndex === 0}
            className="flex items-center gap-1 px-4 py-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeftIcon className="h-5 w-5" />
            Previous
          </button>

          <div className="flex gap-1">
            {stops.slice(
              Math.max(0, currentStopIndex - 2),
              Math.min(stops.length, currentStopIndex + 3)
            ).map((stop, i) => {
              const actualIndex = Math.max(0, currentStopIndex - 2) + i;
              return (
                <button
                  key={actualIndex}
                  onClick={() => setCurrentStopIndex(actualIndex)}
                  className={`w-8 h-8 rounded-full text-sm font-medium ${
                    actualIndex === currentStopIndex
                      ? 'bg-blue-600 text-white'
                      : stop.check_in?.status === 'contacted'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {actualIndex + 1}
                </button>
              );
            })}
          </div>

          <button
            onClick={goToNext}
            disabled={currentStopIndex === stops.length - 1}
            className="flex items-center gap-1 px-4 py-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        </div>
      </footer>

      {/* Stop list drawer */}
      <StopListDrawer
        stops={stops}
        currentIndex={currentStopIndex}
        onStopSelect={setCurrentStopIndex}
        isOpen={showStopList}
        onClose={() => setShowStopList(false)}
        canvassingConfig={canvassingConfig}
      />

      {/* Check-in modal */}
      {showCheckIn && currentStop && (
        <VoterCheckIn
          voter={currentStop}
          customFields={customFields}
          canvassingConfig={canvassingConfig}
          onSubmit={handleCheckIn}
          onClose={() => setShowCheckIn(false)}
          isLoading={checkInMutation.isPending}
        />
      )}

      {/* Add voter modal */}
      {showAddVoter && (
        <AddVoterModal
          datasetId={route?.dataset_id}
          onClose={() => setShowAddVoter(false)}
          onSuccess={handleAddVoter}
          isLoading={addVoterMutation.isPending}
        />
      )}

      {/* Floating action button for adding voters */}
      <button
        onClick={() => setShowAddVoter(true)}
        className="fixed bottom-24 right-4 w-14 h-14 bg-green-600 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-green-700 active:scale-95 transition-transform z-40"
        title="Add new voter"
      >
        <UserPlusIcon className="h-6 w-6" />
      </button>
    </div>
  );
}
